import React from 'react'

import ShareTask from '@SonarCX/components/atoms/ShareTask'
import CommonModal from '@SonarCX/components/organisms/TaskMultiEdit/CommonModal'
import Button from '@SonarCX/components/atoms/Button2'
import Tooltip from '@SonarCX/components/atoms/Tooltip'
import ButtonSecondary from '@SonarCX/components/atoms/ButtonSecondary'

import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { selectDMs } from '@SonarCX/slices/dms'
import { AddSuccessAlert } from '@SonarCX/slices/alert'
import { Routes } from '@SonarCX/Routes'

import useStyles from './styles'

interface ShareTaskModalProps {
  handleOnClose: () => void
  selected: string
}
const ShareTaskModal: React.FC<ShareTaskModalProps> = ({
  handleOnClose,
  selected
}) => {
  const dispatch = useAppDispatch()
  const { dms } = useAppSelector(selectDMs)
  const { allUsers } = useUserAPI()
  const {
    api: { messageHandler }
  } = useSocket()

  const dmsList = React.useMemo(() => Object.values(dms), [dms])
  const [sessions, setSessions] = React.useState<string[]>([])
  const [search, setSearch] = React.useState('')
  const [message, setMessage] = React.useState('')
  const classes = useStyles()

  const filtered = React.useMemo(() => {
    if (!search) {
      return dmsList
    }

    const lowerSearch = search.toLocaleLowerCase()
    return dmsList.filter(dm =>
      (dm.name ?? '').toLocaleLowerCase().includes(lowerSearch)
    )
  }, [dmsList, search])

  const handleClose = React.useCallback(() => {
    setSessions([])
    setSearch('')
    setMessage('')
    handleOnClose()
  }, [handleOnClose])

  const handleAccept = React.useCallback(async () => {
    const linkText = `${window.location.origin}${Routes.TASKS}/${selected}`
    const fullMessage = message ? `${message}\n${linkText}` : linkText

    sessions.forEach(session => {
      const isGroup = !(session in allUsers)
      messageHandler(fullMessage, 'Send', null, session, true, isGroup)
    })

    dispatch(
      AddSuccessAlert(
        `Shared task  with ${sessions.length} chat${
          sessions.length === 1 ? '' : 's'
        }`
      )
    )

    handleClose()
  }, [
    selected,
    message,
    allUsers,
    sessions,
    dispatch,
    handleClose,
    messageHandler
  ])

  const updateSelected = React.useCallback(
    (id: string, isSelected: boolean) => {
      if (isSelected) {
        setSessions(old => old.filter(s => s !== id))
      } else {
        setSessions(old => [...old, id])
      }
    },
    []
  )

  const handleCancel = React.useCallback(() => {
    handleClose()
  }, [handleClose])

  const disabled = sessions.length === 0

  return (
    <CommonModal title='Share' onClose={handleOnClose}>
      <ShareTask
        search={search}
        setSearch={setSearch}
        filtered={filtered}
        sessions={sessions}
        updateSelected={updateSelected}
        message={message}
        setMessage={setMessage}
      />
      <div className={classes.endButtons}>
        <Button onClick={handleCancel}> Cancel</Button>

        <Tooltip title={disabled ? 'Select at least one team chat' : null}>
          <div>
            <ButtonSecondary
              disabled={disabled}
              onClick={handleAccept}
              className={classes.yesButton}
            >
              Send
            </ButtonSecondary>
          </div>
        </Tooltip>
      </div>
    </CommonModal>
  )
}
export default ShareTaskModal
